import type { RootStore } from 'logic';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import type { CardPriority } from '@creative-kit/shared';
import type { RouteNames } from 'logic/routes';

// Route name constants (avoid importing routes.ts value to prevent circular dependency)
// Using type-only import above + casting here breaks the cycle
const ROUTE_SESSION_QUEUE = 'session-queue' as RouteNames;
const ROUTE_SESSION_QUEUE_LEGACY = 'session-queue-legacy' as RouteNames;
const ROUTE_SESSION_STEP = 'session-step' as RouteNames;

export const NEW_SOURCE_TAB = 'NEW_SOURCE_TAB';

// URL step identifiers (readable versions for URL, mapped to internal IDs)
export const URL_STEP_TRIAGE = 'pending';
export const LEGACY_URL_STEP_TRIAGE = 'triage';
export const URL_STEP_DONE = 'archive';
export const URL_STEP_TRASH = 'trash';

// localStorage key for persisting last open step per session
const LAST_STEP_STORAGE_KEY = (sessionId: string) => `cq:lastStepTabId:${sessionId}`;

/**
 * Convert internal step ID to URL-friendly step ID
 */
export function stepIdToUrlStepId(stepId: string): string {
  if (stepId === STEP_TAB_TRIAGE) return URL_STEP_TRIAGE;
  if (stepId === STEP_TAB_DONE) return URL_STEP_DONE;
  return stepId;
}

/**
 * Convert URL step ID to internal step ID
 */
export function urlStepIdToStepId(urlStepId: string): string {
  if (urlStepId === URL_STEP_TRIAGE || urlStepId === LEGACY_URL_STEP_TRIAGE) return STEP_TAB_TRIAGE;
  if (urlStepId === URL_STEP_DONE) return STEP_TAB_DONE;
  return urlStepId;
}

export function getCanonicalQueueRouteStepId({
  sessionId,
  selectedQueueTab,
  selectedStepTabId,
  sessionSteps,
  getStepCardCount,
}: {
  sessionId: string;
  selectedQueueTab: QueueTabName;
  selectedStepTabId: string | null;
  sessionSteps: Array<{ id: string; name: string }>;
  getStepCardCount?: (stepId: string) => number;
}): string {
  if (selectedQueueTab === QueueTabName.Deleted) {
    return URL_STEP_TRASH;
  }

  if (selectedStepTabId) {
    return stepIdToUrlStepId(selectedStepTabId);
  }

  if (selectedQueueTab === QueueTabName.Done) {
    return URL_STEP_DONE;
  }

  let lastStepId: string | null = null;
  try {
    lastStepId = localStorage.getItem(LAST_STEP_STORAGE_KEY(sessionId));
  } catch {
    // localStorage might be unavailable in some contexts
  }

  if (lastStepId) {
    const isValidStep =
      lastStepId === STEP_TAB_TRIAGE ||
      lastStepId === URL_STEP_TRIAGE ||
      lastStepId === LEGACY_URL_STEP_TRIAGE ||
      lastStepId === STEP_TAB_DONE ||
      lastStepId === URL_STEP_DONE ||
      lastStepId === URL_STEP_TRASH ||
      sessionSteps.some((step) => step.id === lastStepId);

    if (!isValidStep) {
      try {
        localStorage.removeItem(LAST_STEP_STORAGE_KEY(sessionId));
      } catch {
        // localStorage might be unavailable in some contexts
      }
      lastStepId = null;
    }
  }

  if (!lastStepId) {
    const firstStepWithCards = sessionSteps.find((step) => (getStepCardCount?.(step.id) ?? 0) > 0);
    return firstStepWithCards?.id ?? URL_STEP_TRIAGE;
  }

  if (lastStepId === URL_STEP_TRASH) {
    return URL_STEP_TRASH;
  }

  return stepIdToUrlStepId(lastStepId);
}

export function shouldDeferQueueRouteRestore({
  stepIdFromUrl,
  hasCurrentSession,
  isStepModeEnabled,
  areSessionCardsLoading,
}: {
  stepIdFromUrl?: string;
  hasCurrentSession: boolean;
  isStepModeEnabled: boolean;
  areSessionCardsLoading: boolean;
}): boolean {
  if (!hasCurrentSession) {
    return true;
  }

  if (stepIdFromUrl) {
    return false;
  }

  if (!isStepModeEnabled) {
    return false;
  }

  return areSessionCardsLoading;
}
export const HOME_SOURCE_TAB = 'home-source-tab';
export const COLLAPSED_ROW_ESTIMATED_SIZE_PX = 122;

// Special tab identifiers for step-aware queue
export const STEP_TAB_TRIAGE = '__triage__'; // Cards with no step assigned
export const STEP_TAB_INBOX = '__triage__'; // Alias for STEP_TAB_TRIAGE (Inbox = cards with no step)
export const STEP_TAB_DONE = '__done__'; // Cards marked as done

export enum QueueTabName {
  InProgress = 'Active',
  Done = 'Done',
  Batch = 'Batch',
  Selected = 'Selected',
  Deleted = 'Deleted',
}

export enum CardView {
  collapsed = 'collapsed',
  expanded = 'expanded',
}

export class UiActiveSessionStore {
  private rootStore: RootStore;
  selectedQueueTab: QueueTabName = QueueTabName.InProgress;
  private selectedSourceTab = '';
  cardView: CardView = CardView.expanded;
  selectedCardIds: string[] = [];
  expandedCardIds: string[] = [];
  batchDeleteLoading = false;
  duplicationOperationLoading = false;
  autofocusSourceRename = false;
  newModuleDialogOpen = false;
  newModulePreselectedSourceId: string | null = null;
  addRowsDialogOpen = false;
  queueViewMenuOpen = false;
  queueViewMenuFocusedModuleId: string | null = null;
  /** When set, QueueViewMenu scopes to this step (e.g. after create-step + open layout). */
  queueViewMenuTargetStepId: string | null = null;
  /** Bumped to re-run layout panel step init while already open. */
  queueViewMenuLayoutInitToken = 0;
  bucketCreationDialogOpen = false;
  createFromScratchDialogOpen = false;
  recentlyCreatedCardIds: string[] = [];
  revealedRowId: string | null = null;
  revealedRowSourceId: string | null = null;

  // Issue filter: show only cards with open issues
  isIssueFilterActive = false;
  // No issues filter: show only cards without open issues (resolved counts as without)
  noIssuesFilterActive = false;

  // Priority filter: show only cards with selected priorities
  priorityFilter: Set<CardPriority> = new Set();

  // No priority filter: show only cards without any priority assigned
  noPriorityFilterActive = false;

  // Has dates filter: show only cards that have at least one date entry
  hasDatesFilterActive = false;
  // No dates filter: show only cards without any date entries
  noDatesFilterActive = false;

  // Multi-Step Queue: Selected step tab ID
  // null = use legacy QueueTabName behavior
  // STEP_TAB_TRIAGE = cards with no step assigned
  // STEP_TAB_DONE = cards marked as done
  // string = specific step ID
  selectedStepTabId: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable<
      this,
      | 'cardView'
      | 'selectedCardIds'
      | 'expandedCardIds'
      | 'autofocusSourceRename'
      | 'selectedQueueTab'
      | 'selectedSourceTab'
      | 'bucketCreationDialogOpen'
      | 'createFromScratchDialogOpen'
      | 'recentlyCreatedCardIds'
      | 'revealedRowId'
      | 'revealedRowSourceId'
      | 'selectedStepTabId'
    >(this, {
      autofocusSourceRename: observable,
      cardView: observable,
      selectedCardIds: observable,
      expandedCardIds: observable,
      toggleExpandedCard: action.bound,
      selectedCardIdsInCurrentStep: computed,
      selectedCards: computed,
      hasSelectedCards: computed,
      selectedQueueTab: observable,
      batchDeleteLoading: observable,
      duplicationOperationLoading: observable,
      addRowsDialogOpen: observable,
      focusCardModule: action.bound,
      toggleSelectedCard: action.bound,
      deselectAllCards: action.bound,
      deselectCard: action.bound,
      deselectCards: action.bound,
      selectAllCardsBelow: action.bound,
      setAddRowsDialogOpen: action.bound,
      setBatchDeleteLoading: action.bound,
      setDuplicationOperationLoading: action.bound,
      selectCardIds: action.bound,
      deselectAllCardsOtherThan: action.bound,
      selectAllOrBelow: action.bound,
      currentQueueTab: computed,
      setCurrentQueueTab: action.bound,
      selectedSourceTab: observable,
      setSelectedSource: action.bound,
      newModuleDialogOpen: observable,
      newModulePreselectedSourceId: observable,
      setNewModuleDialogOpen: action.bound,
      setSourceRenameFocus: action.bound,
      currentSourceTab: computed,
      areSessionCardsLoading: computed,
      queueViewMenuOpen: observable,
      queueViewMenuFocusedModuleId: observable,
      queueViewMenuTargetStepId: observable,
      queueViewMenuLayoutInitToken: observable,
      setQueueViewMenuOpen: action.bound,
      openQueueViewMenuForStep: action.bound,
      setCardView: action.bound,
      toggleCardView: action.bound,
      shouldShowQueueFilter: computed,
      bucketCreationDialogOpen: observable,
      setBucketCreationDialogOpen: action.bound,
      createFromScratchDialogOpen: observable,
      setCreateFromScratchDialogOpen: action.bound,
      recentlyCreatedCardIds: observable,
      addRecentlyCreatedCard: action.bound,
      removeRecentlyCreatedCard: action.bound,
      areTabsDisabled: computed,
      isInProgressTabDisabled: computed,
      isDoneTabDisabled: computed,
      isDeletedTabDisabled: computed,
      revealedRowId: observable,
      revealedRowSourceId: observable,
      setRevealedRow: action.bound,
      clearRevealedRow: action.bound,
      // Issue filter
      isIssueFilterActive: observable,
      toggleIssueFilter: action.bound,
      noIssuesFilterActive: observable,
      toggleNoIssuesFilter: action.bound,
      // Priority filter
      priorityFilter: observable,
      togglePriorityFilter: action.bound,
      clearPriorityFilter: action.bound,
      // No priority filter
      noPriorityFilterActive: observable,
      toggleNoPriorityFilter: action.bound,
      // Has dates filter
      hasDatesFilterActive: observable,
      toggleHasDatesFilter: action.bound,
      noDatesFilterActive: observable,
      toggleNoDatesFilter: action.bound,
      // Clear all quick filters
      clearAllQuickFilters: action.bound,
      hasAnyQuickFilters: computed,
      // Multi-Step Queue
      selectedStepTabId: observable,
      setSelectedStepTab: action.bound,
      navigateToStep: action.bound,
      currentStepTabId: computed,
      isStepModeEnabled: computed,
    });

    reaction(
      // Ensure that you can never deselect the focused card
      () => this.rootStore.sessionsStore.currentSessionId,
      () => this.reset(),
    );

    // Ensure cards are expanded by default when they're loaded
    reaction(
      () => Object.keys(this.rootStore.cardsStore.cards),
      (cardIds) => {
        if (this.cardView === CardView.expanded) {
          this.expandedCardIds = cardIds;
        }
      },
    );

    // Add reactions to keep cards expanded based on current tab
    // DISABLED: This was causing all cards to auto-expand after deletion
    // reaction(
    //   () => ({
    //     activeCards: Object.keys(this.rootStore.cardsStore.cards),
    //     deletedCards: Object.keys(this.rootStore.deletedCardsStore.deletedCards),
    //     currentTab: this.currentQueueTab,
    //   }),
    //   ({ activeCards, deletedCards, currentTab }) => {
    //     let cardIds: string[] = [];
    //
    //     if (currentTab === QueueTabName.Deleted) {
    //       cardIds = deletedCards;
    //     } else {
    //       cardIds = activeCards;
    //     }
    //
    //     if (cardIds.length > 0) {
    //       this.expandedCardIds = cardIds;
    //     }
    //   },
    //   { fireImmediately: true },
    // );

    // Persist selectedStepTabId to localStorage when it changes
    reaction(
      () => ({
        stepId: this.selectedStepTabId,
        sessionId: this.rootStore.sessionsStore.currentSessionId,
      }),
      ({ stepId, sessionId }) => {
        if (sessionId && stepId) {
          try {
            localStorage.setItem(LAST_STEP_STORAGE_KEY(sessionId), stepId);
          } catch {
            // localStorage might be unavailable in some contexts
          }
        }
      },
    );

    // Sync URL params to store when on queue routes
    reaction(
      () => ({
        routeName: this.rootStore.routerStore.getCurrentRoute()?.name,
        params: this.rootStore.routerStore.routerState.params,
        sessionId: this.rootStore.sessionsStore.currentSessionId,
        hasCurrentSession: !!this.rootStore.sessionsStore.currentSession,
        isStepModeEnabled: this.rootStore.sessionsStore.currentSession?.hasSteps ?? false,
        areSessionCardsLoading: this.rootStore.sessionsStore.currentSession
          ? this.areSessionCardsLoading
          : true,
        sessionSteps: this.rootStore.sessionsStore.currentSession?.sortedSteps,
      }),
      ({
        routeName,
        params,
        sessionId,
        hasCurrentSession,
        isStepModeEnabled,
        areSessionCardsLoading,
        sessionSteps,
      }) => {
        if (!sessionId) return;

        const stepIdFromUrl = params.stepId as string | undefined;

        if (
          shouldDeferQueueRouteRestore({
            stepIdFromUrl,
            hasCurrentSession,
            isStepModeEnabled,
            areSessionCardsLoading,
          })
        ) {
          return;
        }

        if (routeName === ROUTE_SESSION_QUEUE_LEGACY) {
          this.rootStore.routerStore.goTo(ROUTE_SESSION_QUEUE, {
            params: stepIdFromUrl ? { sessionId, stepId: stepIdFromUrl } : { sessionId },
            replaceHistory: true,
          });
          return;
        }

        // Handle SESSION_STEP route - redirect to SESSION_QUEUE with same params
        if (routeName === ROUTE_SESSION_STEP && stepIdFromUrl) {
          this.rootStore.routerStore.goTo(ROUTE_SESSION_QUEUE, {
            params: { sessionId, stepId: stepIdFromUrl },
            replaceHistory: true,
          });
          return;
        }

        // Handle SESSION_QUEUE route
        if (routeName === ROUTE_SESSION_QUEUE) {
          if (stepIdFromUrl) {
            // URL has a stepId - sync to store
            this.syncStepFromUrl(stepIdFromUrl, sessionSteps || []);
          } else {
            // No stepId in URL - restore from localStorage (redirect with replace)
            this.restoreLastStepFromStorage(sessionId, sessionSteps || []);
          }
        }
      },
      { fireImmediately: true },
    );
  }

  /**
   * Sync step from URL param to store, with validation
   */
  private syncStepFromUrl(
    urlStepId: string,
    sessionSteps: Array<{ id: string; name: string }>,
  ): void {
    // Handle trash URL
    if (urlStepId === URL_STEP_TRASH) {
      runInAction(() => {
        this.selectedQueueTab = QueueTabName.Deleted;
      });
      // Persist trash to localStorage
      const sessionId = this.rootStore.sessionsStore.currentSessionId;
      if (sessionId) {
        try {
          localStorage.setItem(LAST_STEP_STORAGE_KEY(sessionId), URL_STEP_TRASH);
        } catch {
          // ignore
        }
      }
      return;
    }

    // Convert URL step ID to internal step ID
    const internalStepId = urlStepIdToStepId(urlStepId);
    const canonicalUrlStepId = stepIdToUrlStepId(internalStepId);

    if (canonicalUrlStepId !== urlStepId) {
      const sessionId = this.rootStore.sessionsStore.currentSessionId;
      if (sessionId) {
        this.rootStore.routerStore.goTo(ROUTE_SESSION_QUEUE, {
          params: { sessionId, stepId: canonicalUrlStepId },
          replaceHistory: true,
        });
      }
      return;
    }

    // Validate the step exists (or is a special tab)
    const isValidStep =
      internalStepId === STEP_TAB_TRIAGE ||
      internalStepId === STEP_TAB_DONE ||
      sessionSteps.some((s) => s.id === internalStepId);

    if (isValidStep) {
      runInAction(() => {
        this.setSelectedStepTab(internalStepId);
      });
    } else {
      // Invalid step ID - fallback to triage and normalize URL
      const sessionId = this.rootStore.sessionsStore.currentSessionId;
      if (sessionId) {
        runInAction(() => {
          this.setSelectedStepTab(STEP_TAB_TRIAGE);
        });
        this.rootStore.routerStore.goTo(ROUTE_SESSION_QUEUE, {
          params: {
            sessionId,
            stepId: URL_STEP_TRIAGE,
          },
          replaceHistory: true,
        });
      }
    }
  }

  /**
   * Restore last open step from localStorage and redirect to its URL
   */
  private restoreLastStepFromStorage(
    sessionId: string,
    sessionSteps: Array<{ id: string; name: string }>,
  ): void {
    let lastStepId: string | null = null;

    try {
      lastStepId = localStorage.getItem(LAST_STEP_STORAGE_KEY(sessionId));
    } catch {
      // localStorage might be unavailable
    }

    // If we have a stored step, validate it exists
    if (lastStepId) {
      const isValidStep =
        lastStepId === STEP_TAB_TRIAGE ||
        lastStepId === URL_STEP_TRIAGE ||
        lastStepId === LEGACY_URL_STEP_TRIAGE ||
        lastStepId === STEP_TAB_DONE ||
        lastStepId === URL_STEP_DONE ||
        lastStepId === URL_STEP_TRASH ||
        sessionSteps.some((s) => s.id === lastStepId);

      if (!isValidStep) {
        // Stored step no longer exists (deleted), clear and choose the current default
        lastStepId = null;
        try {
          localStorage.removeItem(LAST_STEP_STORAGE_KEY(sessionId));
        } catch {
          // ignore
        }
      }
    } else {
      // No stored step, choose the current default
      lastStepId = null;
    }

    if (!lastStepId) {
      lastStepId =
        sessionSteps.find((step) => this.currentSession.getCardIdsByStepId(step.id).length > 0)
          ?.id ?? STEP_TAB_TRIAGE;
    }

    // Convert to URL-friendly step ID
    const urlStepId = stepIdToUrlStepId(lastStepId);

    // Set the step in store
    if (lastStepId === URL_STEP_TRASH) {
      runInAction(() => {
        this.selectedQueueTab = QueueTabName.Deleted;
      });
    } else {
      runInAction(() => {
        this.setSelectedStepTab(lastStepId!);
      });
    }

    // Redirect to the step URL (with history replace so Back doesn't bounce)
    this.rootStore.routerStore.goTo(ROUTE_SESSION_QUEUE, {
      params: {
        sessionId,
        stepId: urlStepId,
      },
      replaceHistory: true,
    });
  }

  reset() {
    this.selectedQueueTab = QueueTabName.InProgress;
    this.selectedSourceTab = '';
    this.cardView = CardView.expanded; // Default to expanded
    this.selectedCardIds = [];
    this.batchDeleteLoading = false;
    this.autofocusSourceRename = false;
    this.newModuleDialogOpen = false;
    this.newModulePreselectedSourceId = null;
    this.addRowsDialogOpen = false;
    this.queueViewMenuOpen = false;
    this.revealedRowId = null;
    this.revealedRowSourceId = null;
    this.bucketCreationDialogOpen = false;
    this.createFromScratchDialogOpen = false;
    this.selectedStepTabId = null; // Reset step tab on session change
    // Reset quick filters
    this.isIssueFilterActive = false;
    this.noIssuesFilterActive = false;
    this.priorityFilter = new Set();
    this.noPriorityFilterActive = false;
    this.hasDatesFilterActive = false;
    this.noDatesFilterActive = false;

    // Ensure cards are expanded by default - context aware
    let cardIds: string[] = [];
    if (this.currentQueueTab === QueueTabName.Deleted) {
      cardIds = Object.keys(this.rootStore.deletedCardsStore.deletedCards);
    } else {
      cardIds = Object.keys(this.rootStore.cardsStore.cards);
    }

    // Always expand all cards by default for stable behavior
    this.expandedCardIds = cardIds;
  }

  get shouldShowQueueFilter() {
    return (
      this.rootStore.uiCardSearchStore.hasSearch ||
      this.rootStore.uiModulePowerColumnStore.hasAnyPowerColumnFilters ||
      this.hasAnyQuickFilters
    );
  }

  get hasAnyQuickFilters() {
    return (
      this.isIssueFilterActive ||
      this.noIssuesFilterActive ||
      this.priorityFilter.size > 0 ||
      this.noPriorityFilterActive ||
      this.hasDatesFilterActive ||
      this.noDatesFilterActive
    );
  }

  toggleIssueFilter() {
    this.isIssueFilterActive = !this.isIssueFilterActive;
    if (this.isIssueFilterActive) {
      this.noIssuesFilterActive = false;
    }
  }

  toggleNoIssuesFilter() {
    this.noIssuesFilterActive = !this.noIssuesFilterActive;
    if (this.noIssuesFilterActive) {
      this.isIssueFilterActive = false;
    }
  }

  togglePriorityFilter(priority: CardPriority) {
    const newSet = new Set(this.priorityFilter);
    if (newSet.has(priority)) {
      newSet.delete(priority);
    } else {
      newSet.add(priority);
    }
    this.priorityFilter = newSet;
  }

  clearPriorityFilter() {
    this.priorityFilter = new Set();
  }

  toggleNoPriorityFilter() {
    this.noPriorityFilterActive = !this.noPriorityFilterActive;
  }

  toggleHasDatesFilter() {
    this.hasDatesFilterActive = !this.hasDatesFilterActive;
    if (this.hasDatesFilterActive) {
      this.noDatesFilterActive = false;
    }
  }

  toggleNoDatesFilter() {
    this.noDatesFilterActive = !this.noDatesFilterActive;
    if (this.noDatesFilterActive) {
      this.hasDatesFilterActive = false;
    }
  }

  clearAllQuickFilters() {
    this.isIssueFilterActive = false;
    this.noIssuesFilterActive = false;
    this.priorityFilter = new Set();
    this.noPriorityFilterActive = false;
    this.hasDatesFilterActive = false;
    this.noDatesFilterActive = false;
  }

  get currentSession() {
    return this.rootStore.sessionsStore.currentSession!;
  }

  get currentSourceTab() {
    if (this.selectedSourceTab) {
      return this.selectedSourceTab;
    }

    return HOME_SOURCE_TAB;
  }

  get areSessionCardsLoading() {
    // Need both the cards store list and session card ids loaded to display session cards
    if (!this.currentSession) return true;

    const { cardIds } = this.currentSession.data;
    // Field may be absent on the first snapshot — treat as still loading (not "zero cards loaded")
    if (!Array.isArray(cardIds)) return true;

    // Check if all expected cards are loaded somewhere (either active or deleted stores)
    const allCardsLoaded = cardIds.every(
      (cardId) =>
        this.rootStore.cardsStore.cards[cardId] ||
        this.rootStore.deletedCardsStore.deletedCards[cardId],
    );

    return !allCardsLoaded;
  }

  setCurrentQueueTab(newTab: QueueTabName) {
    this.selectedQueueTab = newTab;
  }

  get currentQueueTab(): QueueTabName {
    return this.selectedQueueTab;
  }

  get areCardsExpanded() {
    return this.cardView === CardView.expanded;
  }

  get areCardsCollapsed() {
    return this.cardView === CardView.collapsed;
  }

  get selectedCards() {
    return this.selectedCardIdsInCurrentStep
      .map((id) =>
        this.currentQueueTab === QueueTabName.Deleted
          ? this.rootStore.deletedCardsStore.deletedCards[id] || this.rootStore.cardsStore.cards[id]
          : this.rootStore.cardsStore.cards[id] ||
            this.rootStore.deletedCardsStore.deletedCards[id],
      )
      .filter(Boolean);
  }

  get expandedCards() {
    return this.expandedCardIds
      .map((id) => {
        // Look in both active and deleted card stores
        return (
          this.rootStore.cardsStore.cards[id] || this.rootStore.deletedCardsStore.deletedCards[id]
        );
      })
      .filter(Boolean); // Filter out undefined cards
  }

  get hasSelectedCards() {
    return this.selectedCardIdsInCurrentStep.length > 0;
  }

  get selectedCardIdsInCurrentStep() {
    if (!this.currentSession) {
      return this.selectedCardIds;
    }

    if (this.currentQueueTab === QueueTabName.Deleted) {
      return this.selectedCardIds.filter(
        (id) => !!this.rootStore.deletedCardsStore.deletedCards[id],
      );
    }

    if (!this.isStepModeEnabled) {
      return this.selectedCardIds;
    }

    const { currentStepTabId } = this;
    return this.selectedCardIds.filter((id) => {
      const card = this.rootStore.cardsStore.cards[id];
      if (!card) return false;

      if (currentStepTabId === STEP_TAB_DONE) {
        return !!card.data.doneAt;
      }

      if (card.data.doneAt) {
        return false;
      }

      if (currentStepTabId === STEP_TAB_TRIAGE) {
        return card.data.stepId === null || card.data.stepId === undefined;
      }

      return card.data.stepId === currentStepTabId;
    });
  }

  isCardSelected = (cardId: string) => this.selectedCardIdsInCurrentStep.includes(cardId);

  private getVisibleCardIdsForCurrentQueueScope(): string[] {
    if (this.currentQueueTab === QueueTabName.Deleted) {
      return this.rootStore.deletedCardsStore.deletedCardsList.map((card) => card.id);
    }

    if (!this.currentSession) return [];

    if (!this.isStepModeEnabled) {
      return this.currentQueueTab === QueueTabName.Done
        ? this.currentSession.doneVisibleCardIds
        : this.currentSession.inProgressVisibleCardIds;
    }

    let scopedCardIds: string[] = [];
    if (this.currentStepTabId === STEP_TAB_DONE) {
      scopedCardIds = this.currentSession.doneCardIdsByStep;
    } else if (this.currentStepTabId === STEP_TAB_TRIAGE) {
      scopedCardIds = this.currentSession.getCardIdsByStepId(null);
    } else {
      scopedCardIds = this.currentSession.getCardIdsByStepId(this.currentStepTabId);
    }

    return this.currentSession.getVisible(scopedCardIds).map((card) => card.id);
  }

  /**
   * When focusing a module,
   * - select card
   * - change source tab to module's source
   * - focus the source search input
   * - update source Action Column (FMA power column) to show this module's connections
   */
  focusCardModule = (cardId: string, moduleId: string) => {
    this.deselectAllCards();
    this.toggleSelectedCard(cardId);
    this.selectModuleSource(moduleId);
  };

  selectModuleSource = (moduleId: string) => {
    const module = this.currentSession?.data.modules[moduleId];
    if (module) {
      this.setSelectedSource(module.sourceId);
      // Disable for now a-la: https://www.notion.so/creativequeue/BUG-Unable-to-input-text-into-Connections-during-editing-50545a3f7ea141089815b80ca00429a1
      // this.rootStore.uiSourceSearchStore.setSearchFocus(true);
      this.rootStore.uiModulePowerColumnStore
        .getSourceModulePowerColumn(module.sourceId)
        .setSelectedModule(module.id);
    }
  };

  toggleSelectedCard = (cardId: string) => {
    if (this.isCardSelected(cardId)) {
      this.selectedCardIds = this.selectedCardIds.filter((ids) => ids !== cardId);
    } else {
      this.selectedCardIds = [...this.selectedCardIds, cardId];
    }
  };

  // If no cards selected, select all. Otherwise select below last selected
  selectAllOrBelow = () => {
    if (this.hasSelectedCards) {
      this.selectAllCardsBelow(
        this.selectedCardIdsInCurrentStep[this.selectedCardIdsInCurrentStep.length - 1],
      );
    } else {
      let ids: string[] = [];

      if (this.currentQueueTab === QueueTabName.Deleted) {
        // For deleted tab, select all deleted cards
        ids = this.rootStore.deletedCardsStore.deletedCardsList.map((card) => card.id);
      } else if (
        this.currentQueueTab === QueueTabName.InProgress ||
        this.currentQueueTab === QueueTabName.Done
      ) {
        ids = this.getVisibleCardIdsForCurrentQueueScope();
      }

      this.selectCardIds(ids);
    }
  };

  selectCardIds = (ids: string[]) => {
    this.selectedCardIds = Array.from(new Set([...this.selectedCardIds, ...ids]));
  };

  deselectAllCardsOtherThan = (cardId: string) => {
    this.selectedCardIds = Array.from(this.selectedCardIds).filter((id) => id == cardId);
  };

  selectAllCardsBelow = (id: string) => {
    let visibleIds: string[] = [];

    if (this.currentQueueTab === QueueTabName.Deleted) {
      // For deleted tab, use deleted cards list
      visibleIds = this.rootStore.deletedCardsStore.deletedCardsList.map((card) => card.id);
    } else {
      visibleIds = this.getVisibleCardIdsForCurrentQueueScope();
    }

    const idx = visibleIds.findIndex((cId) => cId === id);
    if (idx === -1) return;

    if (this.currentQueueTab === QueueTabName.Deleted || this.isStepModeEnabled) {
      // For deleted tab, select from the clicked card to the end
      const rest = visibleIds.slice(idx);
      this.selectedCardIds = [...new Set([...this.selectedCardIds, ...rest])];
    } else {
      // For active/done tabs, use the current card logic
      const currentIdx = this.currentSession!.currentCardIdx;
      const rest = currentIdx <= idx ? visibleIds.slice(idx) : visibleIds.slice(idx, currentIdx);
      this.selectedCardIds = [...new Set([...this.selectedCardIds, ...rest])];
    }
  };

  selectCardsBetween = (
    endId: string,
    startId: string = this.selectedCardIds[this.selectedCardIds.length - 1],
  ) => {
    let visibleIds: string[] = [];

    if (this.currentQueueTab === QueueTabName.Deleted) {
      // For deleted tab, use deleted cards list
      visibleIds = this.rootStore.deletedCardsStore.deletedCardsList.map((card) => card.id);
    } else {
      visibleIds = this.getVisibleCardIdsForCurrentQueueScope();
    }

    const startIdx = visibleIds.findIndex((cId) => cId === startId);
    const endIdx = visibleIds.findIndex((cId) => cId === endId);
    if (startIdx === -1 || endIdx === -1) return;

    /**
     * Reverse order of slice based on whether end is before or after start
     * If endIndex is greater than start, need to add one to capture all of the cards between the range
     */
    const rest =
      endIdx <= startIdx
        ? visibleIds.slice(endIdx, startIdx)
        : visibleIds.slice(startIdx, endIdx + 1);
    this.selectedCardIds = [...new Set([...this.selectedCardIds, ...rest])];
  };

  deselectAllCards = () => {
    this.selectedCardIds = [];
  };

  deselectCard = (cardId: string) => {
    this.selectedCardIds = this.selectedCardIds.filter((id) => id !== cardId);
  };

  deselectCards = (cardIds: string[]) => {
    this.selectedCardIds = this.selectedCardIds.filter((id) => !cardIds.includes(id));
  };

  setSelectedSource(newSourceId: string) {
    this.selectedSourceTab = newSourceId;
  }

  setSourceRenameFocus(value: boolean) {
    this.autofocusSourceRename = value;
  }

  setNewModuleDialogOpen(isOpen: boolean, preselectedSourceId?: string) {
    this.newModuleDialogOpen = isOpen;
    this.newModulePreselectedSourceId = isOpen ? preselectedSourceId ?? null : null;
  }

  setAddRowsDialogOpen(isOpen: boolean) {
    this.addRowsDialogOpen = isOpen;
  }

  setBatchDeleteLoading(isLoading: boolean) {
    this.batchDeleteLoading = isLoading;
  }

  setDuplicationOperationLoading(isLoading: boolean) {
    this.duplicationOperationLoading = isLoading;
  }

  setQueueViewMenuOpen(isOpen: boolean, focusedModuleId?: string) {
    this.queueViewMenuOpen = isOpen;
    this.queueViewMenuFocusedModuleId = isOpen ? focusedModuleId || null : null;
    if (!isOpen) {
      this.queueViewMenuTargetStepId = null;
    }
  }

  openQueueViewMenuForStep(stepId: string) {
    this.queueViewMenuTargetStepId = stepId;
    this.queueViewMenuLayoutInitToken += 1;
    this.queueViewMenuOpen = true;
    this.queueViewMenuFocusedModuleId = null;
  }

  setCardView(view: CardView) {
    this.cardView = view;
  }

  toggleCardView() {
    if (this.areCardsCollapsed) {
      let cardIds: string[] = [];

      if (this.currentQueueTab === QueueTabName.Deleted) {
        // For deleted tab, use deleted cards
        cardIds = Object.keys(this.rootStore.deletedCardsStore.deletedCards);
      } else {
        // For active/done tabs, use regular cards
        cardIds = Object.keys(this.rootStore.cardsStore.cards);
      }

      this.expandedCardIds = cardIds;
      this.cardView = CardView.expanded;
    } else {
      this.expandedCardIds = [];
      this.cardView = CardView.collapsed;
    }
  }

  toggleExpandedCard(cardId: string) {
    if (this.expandedCardIds.includes(cardId)) {
      this.expandedCardIds = this.expandedCardIds.filter((id) => id !== cardId);
    } else {
      this.expandedCardIds = [...this.expandedCardIds, cardId];
    }
  }

  setBucketCreationDialogOpen(isOpen: boolean) {
    this.bucketCreationDialogOpen = isOpen;
  }

  setCreateFromScratchDialogOpen(isOpen: boolean) {
    this.createFromScratchDialogOpen = isOpen;
  }

  get areTabsDisabled() {
    return false;
  }

  get isInProgressTabDisabled() {
    return (
      (this.hasSelectedCards && this.currentQueueTab === QueueTabName.Done) ||
      (this.hasSelectedCards && this.currentQueueTab === QueueTabName.Deleted)
    );
  }

  get isDoneTabDisabled() {
    return (
      (this.hasSelectedCards && this.currentQueueTab === QueueTabName.InProgress) ||
      (this.hasSelectedCards && this.currentQueueTab === QueueTabName.Deleted)
    );
  }

  get isDeletedTabDisabled() {
    return (
      (this.hasSelectedCards && this.currentQueueTab === QueueTabName.InProgress) ||
      (this.hasSelectedCards && this.currentQueueTab === QueueTabName.Done)
    );
  }

  addRecentlyCreatedCard(cardId: string) {
    if (!this.recentlyCreatedCardIds) {
      this.recentlyCreatedCardIds = [];
    }
    if (!this.recentlyCreatedCardIds.includes(cardId)) {
      this.recentlyCreatedCardIds.push(cardId);
      // Auto-remove after 4 seconds
      setTimeout(() => {
        this.removeRecentlyCreatedCard(cardId);
      }, 4000);
    }
  }

  removeRecentlyCreatedCard(cardId: string) {
    if (!this.recentlyCreatedCardIds) {
      return;
    }
    const index = this.recentlyCreatedCardIds.indexOf(cardId);
    if (index > -1) {
      this.recentlyCreatedCardIds.splice(index, 1);
    }
  }

  isCardRecentlyCreated = (cardId: string): boolean => {
    return this.recentlyCreatedCardIds?.includes(cardId) ?? false;
  };

  setRevealedRow = (sourceId: string, rowId: string | null) => {
    this.revealedRowSourceId = sourceId;
    this.revealedRowId = rowId;
  };

  clearRevealedRow = () => {
    this.revealedRowId = null;
    this.revealedRowSourceId = null;
  };

  // ============================================
  // Multi-Step Queue Infrastructure
  // ============================================

  /**
   * Check if the current session has steps enabled
   */
  get isStepModeEnabled(): boolean {
    return this.currentSession?.hasSteps ?? false;
  }

  /**
   * Get the current step tab ID
   * Returns the selected step tab, or falls back to INBOX if in step mode
   */
  get currentStepTabId(): string {
    if (this.selectedQueueTab === QueueTabName.Deleted) {
      return URL_STEP_TRASH;
    }

    if (!this.isStepModeEnabled) {
      // Legacy mode - map QueueTabName to step tab IDs for compatibility
      if (this.selectedQueueTab === QueueTabName.Done) {
        return STEP_TAB_DONE;
      }
      return STEP_TAB_TRIAGE;
    }

    // Step mode - use selectedStepTabId or default to first step
    if (this.selectedStepTabId) {
      return this.selectedStepTabId;
    }

    // Default to Triage - where new cards land
    return STEP_TAB_TRIAGE;
  }

  /**
   * Set the current step tab
   * Also syncs legacy QueueTabName for backwards compatibility
   */
  setSelectedStepTab(stepTabId: string) {
    this.selectedStepTabId = stepTabId;

    // Sync legacy tab for components that still use it
    if (stepTabId === STEP_TAB_DONE) {
      this.selectedQueueTab = QueueTabName.Done;
    } else {
      this.selectedQueueTab = QueueTabName.InProgress;
    }
  }

  /**
   * Navigate to a step URL, updating both the store and the URL
   * This is the preferred method for UI components to use when switching steps
   */
  navigateToStep(stepId: string): void {
    const sessionId = this.rootStore.sessionsStore.currentSessionId;
    if (!sessionId) return;

    // Clear card selection when navigating between steps
    this.selectedCardIds = [];

    // Handle trash navigation
    if (stepId === URL_STEP_TRASH) {
      this.selectedQueueTab = QueueTabName.Deleted;
      // Persist trash to localStorage
      try {
        localStorage.setItem(LAST_STEP_STORAGE_KEY(sessionId), URL_STEP_TRASH);
      } catch {
        // ignore
      }
      this.rootStore.routerStore.goTo(ROUTE_SESSION_QUEUE, {
        params: {
          sessionId,
          stepId: URL_STEP_TRASH,
        },
      });
      return;
    }

    // Set the step in the store (this also persists to localStorage via reaction)
    this.setSelectedStepTab(stepId);

    // Convert to URL-friendly step ID
    const urlStepId = stepIdToUrlStepId(stepId);

    // Navigate to the step URL
    this.rootStore.routerStore.goTo(ROUTE_SESSION_QUEUE, {
      params: {
        sessionId,
        stepId: urlStepId,
      },
    });
  }

  /**
   * Get the step name for a given step ID (used for display text, e.g. toast messages)
   */
  getStepNameById(stepId: string): string {
    if (stepId === STEP_TAB_TRIAGE) return 'Pending';
    if (stepId === STEP_TAB_DONE) return 'Done';
    if (stepId === URL_STEP_TRASH) return 'Trash';

    const step = this.currentSession?.steps?.find((s) => s.id === stepId);
    return step?.name || stepId;
  }
}
